// File: src/components/SiteCard.jsx (No changes needed, already correct based on your description)
import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  LegacyStack as Stack,
  TextContainer,
  Spinner,
  Banner,
  Button,
  UnstyledButton,
  Icon,
} from '@shopify/polaris';
import { XIcon } from '@shopify/polaris-icons';
import { SiteContext } from '../context/SiteContext';
import { AuthContext } from '../context/AuthContext';

const API = process.env.REACT_APP_API_URL || '';

export default function SiteCard({ site, onDelete }) { // Add onDelete prop
  const navigate = useNavigate();
  const { activeSite, setActiveSite, setSites } = useContext(SiteContext);
  const { token } = useContext(AuthContext);

  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete site "${site.name}"?`)) return;

    setDeleting(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/sites/${site.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      
      // Update context
      setSites((prev) => prev.filter((s) => s.id !== site.id));
      if (activeSite?.id === site.id) setActiveSite(null);
      
      // Call parent callback to refresh their local state
      if (onDelete) {
        onDelete(site.id);
      }
      
    } catch (err) {
      console.error(err);
      setError('Failed to delete site.');
    } finally {
      setDeleting(false);
    }
  };

  const handleInstall = (e) => {
    e.stopPropagation();
    // Normalize site.platform to lowercase for consistent matching
    const normalizedPlatform = site.platform.toLowerCase();

    switch (normalizedPlatform) {
      case 'shopify':
        navigate(
          `/connect-shopify?site_id=${site.id}&site_token=${encodeURIComponent(site.token)}`,
          { state: { site } }
        );
        break;
      case 'stanstore':
        navigate(
          `/setup-stan-store?site_id=${site.id}&site_token=${encodeURIComponent(site.token)}`,
          { state: { site } }
        );
        break;
      case 'woocommerce':
        navigate(
          `/connect-woocommerce?site_id=${site.id}&site_token=${encodeURIComponent(site.token)}`,
          { state: { site } }
        );
        break;
      case 'bigcommerce':
        navigate(
          `/connect-bigcommerce` +
          `?site_id=${site.id}` +
          `&site_token=${encodeURIComponent(site.token)}` +
          `&store_hash=${encodeURIComponent(site.store_hash)}`,  // Correctly using site.store_hash
          { state: { site } }
        );
        break;
      default:
        navigate(
          `/install-custom-script?site_id=${site.id}&site_token=${encodeURIComponent(site.token)}`,
          { state: { site } }
        );
    }
  };

  const isActive = activeSite?.id === site.id;

  return (
    <Card
      sectioned
      title={site.name}
      subdued={!isActive}
      onClick={() => setActiveSite(site)}
      style={{
        position: 'relative',
        borderColor: isActive ? '#5c6ac4' : undefined,
        borderWidth: isActive ? '2px' : undefined,
      }}
    >
      <UnstyledButton
        onClick={handleDelete}
        style={{
          position: 'absolute',
          top: 8,
          right: 8,
          padding: 4,
        }}
      >
        {deleting ? <Spinner size="small" /> : <Icon source={XIcon} accessibilityLabel="Delete Site" />}
      </UnstyledButton>

      <TextContainer spacing="tight">
        <Stack vertical spacing="extraTight">
          <div>
            <p>
              <strong>URL:</strong> {site.url}
            </p>
            <p>
              <strong>Platform:</strong> {site.platform}
            </p>
          </div>
          {error && <Banner status="critical">{error}</Banner>}

          <Button primary onClick={handleInstall}>
            {site.platform.toLowerCase() === 'shopify'
              ? 'Connect Shopify'
              : site.platform.toLowerCase() === 'stanstore'
              ? 'Connect Stan.Store'
              : site.platform.toLowerCase() === 'woocommerce'
              ? 'Connect WooCommerce'
              : site.platform.toLowerCase() === 'bigcommerce'
              ? 'Connect BigCommerce'
              : 'Install Tracking Script'}
          </Button>
        </Stack>
      </TextContainer>
    </Card>
  );
}